// ============================================================
// BattlefieldFiringLine - the turn card's facts, drawn where the fight
// is (v8.323.0).
//
// The actor→target line the readout has always described, now drawn:
// eye to eye, dashed in the actor's gold, red when the line is blocked,
// with a chip at its midpoint carrying what the trigger will do -
// `BOLTGUN · S · 62 %`. Information only: the card and the footer are
// the controls, and a button over the map takes space in flow (rejected
// 2026-07-30). Yields to the tape - a measurement the viewer pulled out
// is the more deliberate line.
// ============================================================
import { memo } from 'react';
import type { Camera } from '../engine/battlefield/projection';
import { project, SCENE_VIEW } from '../engine/battlefield/projection';
import type { Vec3 } from '../engine/battlefield/types';
import styles from '../styles/Battlefield.module.css';

interface FiringLineProps {
  from: Vec3;
  to: Vec3;
  label: string;
  blocked: boolean;
  camera: Camera;
}

function BattlefieldFiringLineImpl({ from, to, label, blocked, camera }: FiringLineProps) {
  const a = project(from, camera, SCENE_VIEW);
  const b = project(to, camera, SCENE_VIEW);
  const mx = (a.x + b.x) / 2;
  const my = (a.y + b.y) / 2;
  // Sized to the text: ~6.2px per character at the chip's 10px stencil.
  const w = Math.max(60, label.length * 6.2 + 16);
  return (
    <g data-testid="firing-line" data-blocked={blocked || undefined} className={styles.firingLine}>
      <line
        className={blocked ? styles.firingLineBlocked : styles.firingLineClear}
        x1={a.x}
        y1={a.y}
        x2={b.x}
        y2={b.y}
      />
      <g transform={`translate(${mx.toFixed(1)} ${my.toFixed(1)})`}>
        <rect className={styles.firingChip} x={-w / 2} y={-9} width={w} height={18} rx={3} />
        <text className={styles.firingChipText} x={0} y={4} textAnchor="middle" data-testid="firing-line-label">
          {label}
        </text>
      </g>
    </g>
  );
}

export default memo(BattlefieldFiringLineImpl);
